import { useMutation, useQuery } from "react-query";
import { Auth } from "shared/@auth/Auth";
import createNotification from "shared/@utils/createNotification";

export function useCategoryBuilder() {
  const { makeAuthedRequest } = Auth.useContainer();

  const {
    data: categories,
    isLoading: categoriesIsLoading,
    refetch: refetchCategories,
  } = useQuery(
    ["applications"],
    () =>
      makeAuthedRequest(`/category-builder/applications`, {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    { staleTime: 0 }
  );

  const newApplicationPost = useMutation(
    ["newApplication"],
    async (newAppObject) => {
      const res = await makeAuthedRequest(`/category-builder/applications`, {
        method: "POST",
        body: JSON.stringify(newAppObject),
      });
      const body = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(
          body?.error?.message ||
            body?.message ||
            "Unable to create this application."
        );
      }

      await refetchCategories();
      createNotification({
        type: "success",
        title: "Added",
        message: "New Application Added",
        autoDismiss: true,
      });

      return body;
    }
  );

  const archiveApplicationPut = useMutation(
    ["archiveApplication"],
    async (archiveAppObj) => {
      const res = await makeAuthedRequest(`/category-builder/applications`, {
        method: "PUT",
        body: JSON.stringify(archiveAppObj),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
          body?.error?.message ||
          body?.message ||
          "Unable to update this application.";
        createNotification({
          type: "danger",
          title: "Error",
          message,
          autoDismiss: true,
        });
        return { success: false, message };
      }

      await refetchCategories();
      const isUnarchiving = String(archiveAppObj.isActive) === "true";
      createNotification({
        type: isUnarchiving ? "success" : "warning",
        title: isUnarchiving ? "Unarchived" : "Archived",
        message: isUnarchiving
          ? "Application Unarchived"
          : "Application Archived",
        autoDismiss: true,
      });
      return { success: true };
    }
  );

  return {
    categories,
    categoriesIsLoading,
    refetchCategories,
    newApplicationPost,
    archiveApplicationPut,
  };
}
